import { hashKey, useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import type { LinkedInProfile, SelectedFacetParam } from "common/types";

import { parseListLeadsSearchParams } from "../utils";

import type { ListLeadsSearchParams } from "../types";

interface PagingParams {
  page: number;
  pageSize: number;
}

interface ExcludedListLeadsResponse {
  //the exclusions-search returns plain profiles - the list's own lead fields
  //only apply to the leads it still targets
  people: LinkedInProfile[];
  total: number;
}

async function searchExcludedListLeadsRequest(
  workspaceId: string,
  listId: string,
  searchParams: ListLeadsSearchParams<SelectedFacetParam>,
  { page, pageSize }: PagingParams,
): Promise<ExcludedListLeadsResponse> {
  const queryParams = new URLSearchParams();
  queryParams.set("page", page.toString());
  queryParams.set("size", pageSize.toString());

  const { people, total } = await post(
    `workspaces/${workspaceId}/lists/${listId}/exclusions-search?${queryParams.toString()}`,
    parseListLeadsSearchParams(searchParams),
  );

  return { people, total };
}

/**
 * The leads that were removed from the list, searched through the
 * exclusions-search.
 */
export default function useExcludedListLeads({
  listId,
  searchParams,
  pagingParams,
}: {
  listId: string;
  searchParams: ListLeadsSearchParams<SelectedFacetParam>;
  pagingParams: PagingParams;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { listsKeys } = getQueryKeys(workspaceId);

  const {
    data,
    isFetching: isFetchingExcludedLeads,
    error: excludedLeadsError,
    refetch: refetchExcludedLeads,
  } = useQuery({
    queryKey: listsKeys.leads(listId, true, searchParams, pagingParams),
    queryFn: () =>
      searchExcludedListLeadsRequest(
        workspaceId,
        listId,
        searchParams,
        pagingParams,
      ),
    enabled: !!listId,
    //keeps the current page visible while the next one loads - a different
    //search returns different rows, so nothing is kept across those
    placeholderData: (previousData, previousQuery) => {
      if (previousQuery) {
        const searchQueryKey = listsKeys.leads(listId, true, searchParams);
        if (
          hashKey(previousQuery.queryKey.slice(0, searchQueryKey.length)) ===
          hashKey(searchQueryKey)
        ) {
          return previousData;
        }
      }
    },
    gcTime: 0,
  });

  return {
    excludedLeads: data?.people,
    excludedLeadsTotal: data?.total,
    isFetchingExcludedLeads,
    excludedLeadsError,
    refetchExcludedLeads,
  };
}
